import React, { useState, useEffect } from 'react';
import GlobalModal from '../modals/GlobalModal';
import BackdropSpinner from '../foundation/BackdropSpinner';

export type GlobalModalOptions = {
    footer?: {
        leftBtnText?: string;
        leftBtnClick?: () => void;
        rightBtnText?: string;
        rightBtnClick?: () => void;
    };
};

declare global {
    interface Window {
        showGlobalModal: (
            title: string,
            text: string,
            options?: {
                footer?: {
                    leftBtnText?: string;
                    leftBtnClick?: () => void;
                    rightBtnText?: string;
                    rightBtnClick?: () => void;
                };
            }
        ) => void
        hideGlobalModal: () => void
        showBackdrop: (closable?: boolean) => void
        hideBackdrop: () => void
    }
}

const ModalsManager: React.FC = () => {
    const [modalTitle, setModalTitle] = useState<string | undefined>();
    const [modalText, setModalText] = useState<string | undefined>();
    const [modalOptions, setModalOptions] = useState<GlobalModalOptions | undefined>();
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [backdropCloseable, setBackdropCloseable] = useState<boolean | undefined>();

    const closeGlobalModal = () => {
        setModalText(undefined);
        setModalTitle(undefined);
    };

    const closeBackdrop = () => {
        setShowBackdrop(false);
    };

    const showModal = (title: string, text: string, options?: GlobalModalOptions) => {
        setModalTitle(title);
        setModalText(text);
        setModalOptions(options);
    };

    const showBackdropHandler = (closable = false) => {
        setShowBackdrop(true);
        setBackdropCloseable(closable);
    };

    const hideBackdropHandler = () => {
        setShowBackdrop(false);
        setBackdropCloseable(undefined);
    };

    useEffect(() => {
        window.showGlobalModal = showModal;
        window.hideGlobalModal = closeGlobalModal;
        window.showBackdrop = showBackdropHandler;
        window.hideBackdrop = hideBackdropHandler;
    }, []);

    useEffect(() => {
        const hideRestoredBackdrop = (event: PageTransitionEvent) => {
            if (!event.persisted) {
                return;
            }

            setShowBackdrop(false);
            setBackdropCloseable(undefined);
        };

        window.addEventListener('pageshow', hideRestoredBackdrop);

        return () => {
            window.removeEventListener('pageshow', hideRestoredBackdrop);
        };
    }, []);

    return (
        <>
            <BackdropSpinner
                open={showBackdrop}
                onClose={backdropCloseable ? closeBackdrop : undefined}
            />
            <GlobalModal
                onClose={closeGlobalModal}
                open={!!modalText}
                text={modalText}
                title={modalTitle}
                leftBtnClick={modalOptions?.footer?.leftBtnClick}
                leftBtnText={modalOptions?.footer?.leftBtnText}
                rightBtnClick={modalOptions?.footer?.rightBtnClick}
                rightBtnText={modalOptions?.footer?.rightBtnText}
            />
        </>
    );
};

export default ModalsManager;
